import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthV2 } from '@/contexts/AuthContextV2';

const MainLayout = lazy(() =>
  import('@/layouts/MainLayout').then(({ MainLayout }) => ({ default: MainLayout }))
);
const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then(({ LoginPage }) => ({ default: LoginPage }))
);
const ImpersonatePage = lazy(() =>
  import('@/pages/auth/ImpersonatePage').then(({ ImpersonatePage }) => ({
    default: ImpersonatePage,
  }))
);
const TrialExpiredPage = lazy(() =>
  import('@/pages/trial/TrialExpiredPage').then(({ TrialExpiredPage }) => ({
    default: TrialExpiredPage,
  }))
);
const VerifyEmailPage = lazy(() =>
  import('@/pages/trial/VerifyEmailPage').then(({ VerifyEmailPage }) => ({ default: VerifyEmailPage }))
);
const UsersPage = lazy(() =>
  import('@/pages/users/UsersPage').then(({ UsersPage }) => ({ default: UsersPage }))
);
const CustomersPage = lazy(() =>
  import('@/pages/customers/CustomersPage').then(({ CustomersPage }) => ({
    default: CustomersPage,
  }))
);
const CustomerDetailPage = lazy(() =>
  import('@/pages/customers/CustomerDetailPage').then(({ CustomerDetailPage }) => ({
    default: CustomerDetailPage,
  }))
);
const BatchEntryImportPage = lazy(() =>
  import('@/pages/entries/BatchEntryImportPage').then(({ BatchEntryImportPage }) => ({
    default: BatchEntryImportPage,
  }))
);
const BatchInstallmentImportPage = lazy(() =>
  import('@/pages/installments/BatchInstallmentImportPage').then(
    ({ BatchInstallmentImportPage }) => ({ default: BatchInstallmentImportPage })
  )
);
const BatchReceiptImportPage = lazy(() =>
  import('@/pages/receipts/BatchReceiptImportPage').then(({ BatchReceiptImportPage }) => ({
    default: BatchReceiptImportPage,
  }))
);
const TimeTrackingDashboardPage = lazy(() =>
  import('@/pages/tracking/TimeTrackingDashboardPage').then(({ TimeTrackingDashboardPage }) => ({
    default: TimeTrackingDashboardPage,
  }))
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then(({ NotFoundPage }) => ({ default: NotFoundPage }))
);
const ContaAzulCallbackPage = lazy(() =>
  import('@/components/conta-azul/ContaAzulCallbackPage').then(({ ContaAzulCallbackPage }) => ({
    default: ContaAzulCallbackPage,
  }))
);

const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: 'background.default',
    }}
  >
    <CircularProgress />
  </Box>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuthV2();
  const isExpiredTrial =
    user?.company?.accountMode === 'trial' &&
    (user.company.trialLifecycleStatus === 'expired' ||
      user.company.trialLifecycleStatus === 'cleaned');

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isExpiredTrial) {
    return <Navigate to="/trial-expirado" replace />;
  }

  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuthV2();
  const isExpiredTrial =
    user?.company?.accountMode === 'trial' &&
    (user.company.trialLifecycleStatus === 'expired' ||
      user.company.trialLifecycleStatus === 'cleaned');

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    if (isExpiredTrial) {
      return <Navigate to="/trial-expirado" replace />;
    }
    return <Navigate to="/empresas" replace />;
  }

  return <>{children}</>;
};

const ExpiredTrialRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuthV2();
  const isExpiredTrial =
    user?.company?.accountMode === 'trial' &&
    (user.company.trialLifecycleStatus === 'expired' ||
      user.company.trialLifecycleStatus === 'cleaned');

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isExpiredTrial) {
    return <Navigate to="/empresas" replace />;
  }

  return <>{children}</>;
};

export const Router = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/verificar-email" element={<VerifyEmailPage />} />
        <Route
          path="/trial-expirado"
          element={
            <ExpiredTrialRoute>
              <TrialExpiredPage />
            </ExpiredTrialRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/empresas" replace />} />
          <Route path="empresas" element={<CustomersPage />} />
          <Route path="empresas/:id" element={<CustomerDetailPage />} />
          <Route path="usuarios" element={<UsersPage />} />
          <Route path="dashboard" element={<TimeTrackingDashboardPage />} />
          <Route
            path="empresa/:customerId/comprovantes/importacao-lote"
            element={<BatchReceiptImportPage />}
          />
          <Route
            path="empresa/:customerId/lancamentos/importacao-lote"
            element={<BatchEntryImportPage />}
          />
          <Route
            path="empresa/:customerId/boletos/importacao-lote"
            element={<BatchInstallmentImportPage />}
          />
        </Route>
        {/* OAuth callback (standalone, no layout) */}
        <Route path="/conta-azul/callback" element={<ContaAzulCallbackPage />} />
        {/* Impersonation exchange (público — troca code por cookie, depois redireciona) */}
        <Route path="/impersonate" element={<ImpersonatePage />} />
        {/* Catch-all route for 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};
